import { cn } from '@/lib/cn'

/**
 * Deterministic hue from a cuisine name.
 *
 * The same cuisine always gets the same hue, including ones not in the seeded
 * list, so a new cuisine needs no code change. A rolling `hash * 31` followed by
 * a multiply-shift spreads the seeded cuisines far better than the plain hash
 * did, which clustered them within a few degrees of one another.
 *
 * Hues cover a 300-degree arc rather than the full circle: the excluded band is
 * where the pure yellow-greens live, and those read as a status colour sitting
 * next to a green accent rather than as an identity.
 *
 * Two cuisines can still land close together. That is acceptable because the
 * hue is decoration — the name is always written out beside the dot, so the
 * colour is never the only thing identifying a cuisine.
 */
function hueFor(cuisine: string | null | undefined): number {
  if (!cuisine) return 265
  let hash = 0
  for (let index = 0; index < cuisine.length; index += 1) {
    hash = (Math.imul(hash, 31) + cuisine.charCodeAt(index)) >>> 0
  }
  // Take the high bits of hash * the 64-bit golden ratio: the low bits of a
  // multiply are the poorly distributed ones.
  const spread = (BigInt(hash) * 0x9e3779b97f4a7c15n) >> 40n
  return Number(spread % 300n) + 20
}

/**
 * Colour accent for a cuisine.
 *
 * The wash is decorative only — text inside it stays on --color-surface, so
 * contrast does not depend on which hue was picked. That is why the chroma is
 * kept low enough to be safe in both schemes.
 */
export function CuisineAccent({
  cuisine,
  className,
}: {
  cuisine: string | null | undefined
  className?: string
}) {
  const hue = hueFor(cuisine)
  return (
    <span
      aria-hidden
      style={
        {
          '--cuisine-hue': hue,
        } as React.CSSProperties
      }
      className={cn(
        'bg-[oklch(var(--cuisine-wash-l)_var(--cuisine-wash-c)_var(--cuisine-hue))]',
        className,
      )}
    />
  )
}

/** Small hue chip used in dense lists where a full wash would be too heavy. */
export function CuisineDot({
  cuisine,
  className,
}: {
  cuisine: string | null | undefined
  className?: string
}) {
  const hue = hueFor(cuisine)
  return (
    <span
      aria-hidden
      style={
        {
          '--cuisine-hue': hue,
        } as React.CSSProperties
      }
      className={cn(
        'size-2 shrink-0 rounded-full bg-[oklch(var(--cuisine-dot-l)_var(--cuisine-dot-c)_var(--cuisine-hue))]',
        className,
      )}
    />
  )
}
